/**
 * Status rail (Home v2): the one place balances appear. Sits at the top of every tab-root screen.
 * Left: membership tier badge (gem in the tier colour + name) or a muted gem and "Join" when there is none → Shop › Membership.
 * Right: cowrie + Boli ("+" → Boli packs), then the DC coin + tokens ("+" → Tokens); no tokens shows in the alert colour.
 */
import type { ReactElement } from "react";
import { Link } from "react-router-dom";
import { MEMBERSHIP_TIER_NAMES, type MembershipTierT } from "@digu/shared";
import { Icon } from "./Icon";
import { Cowrie } from "./Money";
import { formatNumber } from "../lib/format";
import { useSession } from "../lib/session";

export const tierColor = (tier: MembershipTierT): string => `var(--tier-${tier})`;

/** The token glyph: a photographed gold coin stamped DC (D-081). */
export function DcCoin({ size = 20, className = "" }: { size?: number; className?: string }): ReactElement {
  return <img className={`dc-coin currency-icon ${className}`} src="/art/v5/glyphs/token.svg" width={size} height={size} alt="" aria-hidden="true" draggable={false} />;
}

export function TierGem({ tier, size = 22, className = "" }: { tier: MembershipTierT; size?: number; className?: string }): ReactElement {
  return <Icon name="gem" size={size} className={className} style={{ color: tierColor(tier) }} label={`${MEMBERSHIP_TIER_NAMES[tier]} member`} />;
}

export function StatusRail({ className = "" }: { className?: string }): ReactElement {
  const { account, wallet } = useSession();
  const tier = account?.membership.active ? account.membership.tier : null;
  const boli = wallet?.boli ?? 0;
  const tokens = wallet?.tokens ?? 0;
  return (
    <div className={`rail ${className}`} role="region" aria-label="Your status">
      <Link to="/shop?tab=membership" className={`rail__tier ${tier ? "rail__tier--on" : ""}`} aria-label={tier ? `${MEMBERSHIP_TIER_NAMES[tier]} membership` : "Get a membership in the Shop"} data-testid="rail-tier">
        <span className="rail__disc" style={tier ? { boxShadow: `inset 0 0 0 1.5px ${tierColor(tier)}` } : undefined}>
          {tier ? <TierGem tier={tier} /> : <Icon name="gem" size={22} className="rail__lock" />}
        </span>
        {tier ? <span className="rail__tier-name" style={{ color: tierColor(tier) }}>{MEMBERSHIP_TIER_NAMES[tier]}</span> : <span className="rail__tier-name rail__join">Get a<br />membership</span>}
      </Link>
      <div className="rail__balances">
        <span className="rail__balance" aria-label={`${formatNumber(boli)} Boli`}>
          <Cowrie size={20} className="rail__cowrie" />
          <span className="num-s rail__num" aria-hidden="true">{formatNumber(boli)}</span>
          <Link to="/shop?tab=boli" className="rail__plus" aria-label="Get more Boli"><Icon name="plus" size={18} /></Link>
        </span>
        <span className="rail__balance" aria-label={`${formatNumber(tokens)} tokens`}>
          <DcCoin />
          <span className={`num-s rail__num ${tokens === 0 ? "rail__num--empty" : ""}`} aria-hidden="true" data-testid="rail-tokens">{formatNumber(tokens)}</span>
          <Link to="/shop?tab=tokens" className={`rail__plus ${tokens === 0 ? "rail__plus--hot" : ""}`} aria-label="Get more tokens"><Icon name="plus" size={18} /></Link>
        </span>
      </div>
    </div>
  );
}
